/**
 * Mirror of `backend/utils/billing.js`, so the figure an admin sees while editing an
 * invoice is exactly the figure the server will store. Keep the two in step.
 *
 *   taxable = subtotal + extraCharges          (extraCharges is always >= 0)
 *   gst     = round(taxable * gstPercentage / 100)
 *   total   = taxable + gst - discount         (discount is always >= 0, never below 0 total)
 */

const round = (n) => Math.round((Number(n) || 0) * 100) / 100;

export function computeBillTotals({
  subtotal = 0,
  extraCharges = 0,
  discount = 0,
  gstPercentage = 0
} = {}) {
  const base = Math.max(0, Number(subtotal) || 0);
  const extra = Math.max(0, Number(extraCharges) || 0);
  const pct = Math.max(0, Number(gstPercentage) || 0);

  const taxableAmount = base + extra;
  const gstAmount = Math.round((taxableAmount * pct) / 100);
  const grossAmount = taxableAmount + gstAmount;
  const appliedDiscount = Math.min(Math.max(0, Number(discount) || 0), grossAmount);

  return {
    subtotal: round(base),
    extraCharges: round(extra),
    taxableAmount: round(taxableAmount),
    gstPercentage: pct,
    gstAmount: round(gstAmount),
    grossAmount: round(grossAmount),
    discount: round(appliedDiscount),
    totalAmount: round(grossAmount - appliedDiscount)
  };
}

/** Keep a money input to digits and at most one decimal point; never allows a minus sign. */
export const sanitiseAmountInput = (value) => {
  const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  return rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
};

export const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;
